import { Button, Card, Input, Select } from "@swot/base-components";
import { FiPlusCircle, FiRefreshCw, FiSearch } from "react-icons/fi";

export type CmtsMasterDataActiveFilter = "active" | "inactive" | "all";

export type CmtsMasterDataToolbarState = {
  readonly search: string;
  readonly activeFilter: CmtsMasterDataActiveFilter;
  readonly sortBy: string;
  readonly sortDirection: "asc" | "desc";
  readonly page: number;
  readonly pageSize: number;
};

export function CmtsMasterDataToolbar(props: {
  readonly title: string;
  readonly subtitle: string;
  readonly state: CmtsMasterDataToolbarState;
  readonly canCreate: boolean;
  readonly canCreateReason?: string | undefined;
  readonly busy?: boolean | undefined;
  readonly totalCount: number;
  readonly onCreate: () => void;
  readonly onRefresh: () => void | Promise<void>;
  readonly onStateChange: (state: CmtsMasterDataToolbarState) => void;
}) {
  const update = (patch: Partial<CmtsMasterDataToolbarState>) => props.onStateChange({ ...props.state, ...patch });
  const totalPages = Math.max(1, Math.ceil(props.totalCount / props.state.pageSize));
  return (
    <Card title={props.title} subtitle={props.subtitle}>
      <section className="cmts-page-action-bar cmts-master-data-toolbar" aria-label="Master-data table controls">
        <div className="cmts-master-data-toolbar__filters">
          <Input label="Search" value={props.state.search} placeholder="Search code, label, description" onChange={(event) => update({ search: event.currentTarget.value, page: 1 })} />
          <Select
            label="Status filter"
            value={props.state.activeFilter}
            onChange={(event) => update({ activeFilter: event.currentTarget.value as CmtsMasterDataActiveFilter, page: 1 })}
            options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "all", label: "All" }]}
          />
          <Select
            label="Sort by"
            value={props.state.sortBy}
            onChange={(event) => update({ sortBy: event.currentTarget.value, page: 1 })}
            options={[{ value: "sortOrder", label: "Sort order" }, { value: "code", label: "Code" }, { value: "label", label: "Label" }, { value: "updatedAt", label: "Updated" }]}
          />
          <Select
            label="Direction"
            value={props.state.sortDirection}
            onChange={(event) => update({ sortDirection: event.currentTarget.value as "asc" | "desc", page: 1 })}
            options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]}
          />
          <Select
            label="Page size"
            value={String(props.state.pageSize)}
            onChange={(event) => update({ pageSize: Number(event.currentTarget.value), page: 1 })}
            options={[10, 25, 50, 100].map((value) => ({ value: String(value), label: `${value} rows` }))}
          />
        </div>
        <div className="cmts-page-actions">
          <Button label="Apply search" variant="secondary" leftIcon={<FiSearch />} disabled={Boolean(props.busy)} onClick={() => update({ page: 1 })} />
          <Button label="Refresh" variant="secondary" leftIcon={<FiRefreshCw />} disabled={Boolean(props.busy)} onClick={props.onRefresh} />
          <Button label="Create" leftIcon={<FiPlusCircle />} disabled={!props.canCreate || Boolean(props.busy)} title={props.canCreateReason} onClick={props.onCreate} />
        </div>
      </section>
      <footer className="cmts-master-data-toolbar__paging" aria-label="Server pagination controls">
        <Button label="Previous page" variant="secondary" disabled={props.state.page <= 1 || Boolean(props.busy)} onClick={() => update({ page: Math.max(1, props.state.page - 1) })} />
        <span>Page {props.state.page} of {totalPages} · {props.totalCount} backend rows</span>
        <Button label="Next page" variant="secondary" disabled={props.state.page >= totalPages || Boolean(props.busy)} onClick={() => update({ page: Math.min(totalPages, props.state.page + 1) })} />
      </footer>
    </Card>
  );
}
